import React, { useState, useRef, useEffect } from 'react';
import {
  FiCamera,
  FiUpload,
  FiRefreshCw,
  FiRotateCcw as FiRotate,
  FiDownload,
  FiSliders,
  FiZap,
  FiLayers,
  FiCheckCircle,
  FiAlertCircle,
  FiInfo,
  FiSun,
  FiCircle,
  FiAperture,
  FiCloud,
  FiMoon,
  FiCoffee,
  FiStar,
  FiRepeat,
  FiImage
} from 'react-icons/fi';

const StudioMusuPhotolab = () => {
  const [photo, setPhoto] = useState("https://picsum.photos/600/400");
  const [activeTab, setActiveTab] = useState('presets');
  const [filters, setFilters] = useState({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    hue: 0,
    blur: 0,
    grayscale: 0,
    sepia: 0,
    invert: 0,
    red: 100,
    green: 100,
    blue: 100
  });
  const [galleryPhotos, setGalleryPhotos] = useState([]);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [activePreset, setActivePreset] = useState(null);

  const fileInputRef = useRef(null);
  const photoRef = useRef(null);
  const canvasRef = useRef(null);

  // Show notification
  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 3000);
  };

  // Apply filters when they change
  useEffect(() => {
    if (photoRef.current) {
      const filterString = `
        brightness(${filters.brightness}%) 
        contrast(${filters.contrast}%) 
        saturate(${filters.saturation}%) 
        hue-rotate(${filters.hue}deg) 
        blur(${filters.blur}px) 
        grayscale(${filters.grayscale}%) 
        sepia(${filters.sepia}%) 
        invert(${filters.invert}%)
      `;
      photoRef.current.style.filter = filterString;
    }
  }, [filters, photo]);

  // Load gallery photos on component mount
  useEffect(() => {
    const newPhotos = [];
    for (let i = 0; i < 20; i++) {
      newPhotos.push(`https://picsum.photos/600/400?random=${i}`);
    }
    setGalleryPhotos(newPhotos);
  }, []);

  const handleDownload = () => {
    if (!photoRef.current) return;
    
    try {
      const canvas = canvasRef.current || document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = photoRef.current;
      
      // Set canvas dimensions to match the image
      canvas.width = img.naturalWidth || img.width;
      canvas.height = img.naturalHeight || img.height;
      
      // Apply the same CSS filters to the canvas
      ctx.filter = `
        brightness(${filters.brightness}%) 
        contrast(${filters.contrast}%) 
        saturate(${filters.saturation}%) 
        hue-rotate(${filters.hue}deg) 
        blur(${filters.blur}px) 
        grayscale(${filters.grayscale}%) 
        sepia(${filters.sepia}%) 
        invert(${filters.invert}%)
      `;
      
      // Draw the image on canvas
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Create download link
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = "studio_musu_edited_photo.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showNotification('Edited photo downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error downloading image:', error);
      showNotification('Failed to download edited photo', 'error');
    }
  };

  const handleUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (!file.type.match('image.*')) {
      showNotification('Please select an image file.', 'error');
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhoto(ev.target.result);
      resetFilters();
      showNotification('Photo uploaded successfully!', 'success');
    };
    reader.readAsDataURL(file);
  };

  const handleRandomPhoto = () => {
    const url = `https://picsum.photos/600/400?random=${Date.now()}`;
    setPhoto(url);
    resetFilters();
    showNotification('Random photo loaded!', 'success');
  };

  const resetFilters = () => {
    setFilters({
      brightness: 100,
      contrast: 100,
      saturation: 100,
      hue: 0,
      blur: 0,
      grayscale: 0,
      sepia: 0,
      invert: 0,
      red: 100,
      green: 100,
      blue: 100
    });
    setActivePreset(null);
    showNotification('Filters reset to default', 'info');
  };

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: parseInt(value, 10)
    }));
    setActivePreset(null);
  };

  const applyPreset = (presetName) => {
    let newFilters = { ...filters };

    switch (presetName) {
      case 'vintage':
        newFilters.sepia = 70;
        newFilters.contrast = 120;
        newFilters.brightness = 110;
        newFilters.saturation = 85;
        break;
      case 'blackAndWhite':
        newFilters.grayscale = 100;
        newFilters.contrast = 130;
        break;
      case 'warm':
        newFilters.hue = 30;
        newFilters.saturation = 120;
        newFilters.brightness = 105;
        break;
      case 'cool':
        newFilters.hue = 200;
        newFilters.saturation = 90;
        newFilters.blue = 120;
        break;
      case 'dramatic':
        newFilters.contrast = 150;
        newFilters.brightness = 90;
        newFilters.saturation = 110;
        break;
      case 'bright':
        newFilters.brightness = 150;
        newFilters.saturation = 120;
        newFilters.contrast = 110;
        break;
      default:
        break;
    }

    setFilters(newFilters);
    setActivePreset(presetName);
    const cfg = presetConfigs.find(p => p.name === presetName);
    showNotification(`${cfg ? cfg.label : 'Preset'} filter applied`, 'success');
  };

  // Helper functions for filter controls
  const getFilterIcon = (filter) => {
    const icons = {
      brightness: FiSun,
      contrast: FiCircle,
      saturation: FiAperture,
      hue: FiRotate,
      blur: FiCloud,
      grayscale: FiMoon,
      sepia: FiCoffee,
      invert: FiRepeat,
      red: FiCircle,
      green: FiCircle,
      blue: FiCircle
    };
    return icons[filter] || FiSliders;
  };

  const getFilterColor = (filter) => {
    const colors = {
      brightness: 'text-yellow-400',
      contrast: 'text-blue-400',
      saturation: 'text-green-400',
      hue: 'text-purple-400',
      blur: 'text-gray-400',
      grayscale: 'text-gray-300',
      sepia: 'text-amber-400',
      invert: 'text-red-400',
      red: 'text-red-400',
      green: 'text-green-400',
      blue: 'text-blue-400'
    };
    return colors[filter] || 'text-gray-300';
  };

  const getFilterLabel = (filter) => {
    const labels = {
      brightness: 'Brightness',
      contrast: 'Contrast',
      saturation: 'Saturation',
      hue: 'Hue Rotation',
      blur: 'Blur',
      grayscale: 'Grayscale',
      sepia: 'Sepia',
      invert: 'Invert',
      red: 'Red Channel',
      green: 'Green Channel',
      blue: 'Blue Channel'
    };
    return labels[filter] || filter;
  };

  const getFilterUnit = (filter) => {
    if (filter === 'hue') return 'deg';
    if (filter === 'blur') return 'px';
    return '%';
  };

  const getFilterMin = (filter) => {
    if (filter === 'hue') return 0;
    if (filter === 'blur') return 0;
    return 0;
  };

  const getFilterMax = (filter) => {
    if (filter === 'hue') return 360;
    if (filter === 'blur') return 20;
    return 200;
  };

  const presetConfigs = [
    { name: 'vintage', label: 'Vintage', icon: FiCoffee, color: 'from-amber-700 to-amber-900', iconColor: 'text-amber-300' },
    { name: 'blackAndWhite', label: 'B&W', icon: FiMoon, color: 'from-gray-700 to-gray-900', iconColor: 'text-gray-300' },
    { name: 'warm', label: 'Warm', icon: FiSun, color: 'from-orange-600 to-red-800', iconColor: 'text-orange-300' },
    { name: 'cool', label: 'Cool', icon: FiCloud, color: 'from-blue-600 to-indigo-800', iconColor: 'text-blue-300' },
    { name: 'dramatic', label: 'Dramatic', icon: FiStar, color: 'from-purple-700 to-purple-900', iconColor: 'text-purple-300' },
    { name: 'bright', label: 'Bright', icon: FiSun, color: 'from-yellow-500 to-yellow-700', iconColor: 'text-yellow-200' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-indigo-900 text-white">
      {/* Hidden canvas for image processing */}
      <canvas ref={canvasRef} className="hidden" />
      
      {/* Notification System */}
      {notification.show && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center space-x-2 transition-all duration-300 ${notification.type === 'success' ? 'bg-green-800 text-green-100' :
          notification.type === 'error' ? 'bg-red-800 text-red-100' :
            'bg-blue-800 text-blue-100'
          }`}>
          {notification.type === 'success' && <FiCheckCircle className="mr-2" />}
          {notification.type === 'error' && <FiAlertCircle className="mr-2" />}
          {notification.type === 'info' && <FiInfo className="mr-2" />}
          <span>{notification.message}</span>
        </div>
      )}

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <header className="text-center mb-12 mt-20">
          <div className="flex items-center justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-r from-purple-600 to-pink-500 rounded-full flex items-center justify-center mr-3">
              <FiCamera className="text-white text-xl" />
            </div>
            <h1 className="text-4xl font-bold text-white">Studio Musu</h1>
          </div>
          <p className="text-yellow-400 text-sm uppercase tracking-wider mb-6">Photography</p>

          <p className="max-w-2xl mx-auto text-lg font-medium bg-gradient-to-r from-purple-200 via-pink-200 to-blue-200 bg-clip-text text-transparent">
            Edit your photos with professional-grade tools. Adjust colors, apply filters, and transform your images with real-time preview.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Image and Actions */}
          <div className="lg:col-span-2">
            <div className="bg-gray-800 bg-opacity-50 backdrop-blur-md rounded-2xl p-4 sm:p-6 shadow-xl border border-gray-700">
              {/* Photo preview */}
              <div className="bg-gray-900 rounded-xl p-3 sm:p-4 flex items-center justify-center min-h-[250px] sm:min-h-[400px] mb-6">
                <img
                  ref={photoRef}
                  src={photo}
                  alt="Your photo"
                  className="rounded-lg max-w-full max-h-[250px] sm:max-h-[400px] object-contain"
                  crossOrigin="anonymous"
                  onLoad={() => {
                    // Ensure the canvas is ready when the image loads
                    if (canvasRef.current && photoRef.current) {
                      canvasRef.current.width = photoRef.current.naturalWidth;
                      canvasRef.current.height = photoRef.current.naturalHeight;
                    }
                  }}
                />
              </div>

              {/* Action buttons */}
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center mb-6 w-full">
                <input
                  type="file"
                  ref={fileInputRef}
                  id="fileUpload"
                  accept="image/*"
                  className="hidden"
                  onChange={handleUpload}
                />
                <button
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  className="w-full sm:w-auto px-5 py-3 rounded-xl font-semibold flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 transition-all shadow-lg hover:shadow-blue-500/20"
                >
                  <FiUpload className="mr-2" /> Upload Photo
                </button>
                <button
                  onClick={handleRandomPhoto}
                  className="w-full sm:w-auto px-5 py-3 rounded-xl font-semibold flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-500 hover:to-pink-400 transition-all shadow-lg hover:shadow-purple-500/20"
                >
                  <FiRefreshCw className="mr-2" /> Random Photo
                </button>
                <button
                  onClick={resetFilters}
                  className="w-full sm:w-auto px-5 py-3 rounded-xl font-semibold flex items-center justify-center bg-gradient-to-r from-gray-700 to-gray-600 hover:from-gray-600 hover:to-gray-500 transition-all shadow-lg hover:shadow-gray-500/20"
                >
                  <FiRotate className="mr-2" /> Reset
                </button>
              </div>

              {/* Download button */}
              <button
                onClick={handleDownload}
                className="w-full px-6 py-4 rounded-xl font-semibold text-center flex items-center justify-center bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 transition-all shadow-lg hover:shadow-green-500/20"
              >
                <FiDownload className="mr-2" /> Download Edited Photo
              </button>
            </div>
          </div>

          {/* Right Column: Filter Controls */}
          <div className="lg:col-span-1">
            <div className="bg-gray-800 bg-opacity-50 backdrop-blur-md rounded-2xl p-6 shadow-xl border border-gray-700 h-full">
              {/* Tabs */}
              <div className="flex mb-6 border-b border-gray-700 overflow-x-auto whitespace-nowrap scrollbar-hide">
                <button
                  className={`py-3 px-4 border-b-2 flex-none flex items-center justify-center min-w-[100px] lg:flex-1 lg:min-w-0 ${activeTab === 'basic'
                    ? 'border-purple-500 text-purple-400'
                    : 'border-transparent text-white hover:text-gray-300'
                    }`}
                  onClick={() => setActiveTab('basic')}
                >
                  <FiSliders className="mr-2" /> Basic
                </button>
                <button
                  className={`py-3 px-4 border-b-2 flex-none flex items-center justify-center min-w-[100px] lg:flex-1 lg:min-w-0 ${activeTab === 'effects'
                    ? 'border-purple-500 text-purple-400'
                    : 'border-transparent text-white hover:text-gray-300'
                    }`}
                  onClick={() => setActiveTab('effects')}
                >
                  <FiZap className="mr-2" /> Effects
                </button>
                <button
                  className={`py-3 px-4 border-b-2 flex-none flex items-center justify-center min-w-[100px] lg:flex-1 lg:min-w-0 ${activeTab === 'presets'
                    ? 'border-purple-500 text-purple-400'
                    : 'border-transparent text-white hover:text-gray-300'
                    }`}
                  onClick={() => setActiveTab('presets')}
                >
                  <FiLayers className="mr-2" /> Presets
                </button>
              </div>

              {/* Tab Content */}
              <div className="max-h-[500px] overflow-y-auto pr-2">
                {/* Basic Adjustments Tab */}
                {activeTab === 'basic' && (
                  <div className="tab-panel">
                    <h2 className="text-xl font-semibold mb-4 text-purple-400">Basic Adjustments</h2>

                    {['brightness', 'contrast', 'saturation', 'hue', 'blur'].map(filter => {
                      const Icon = getFilterIcon(filter);
                      return (
                        <div key={filter} className="filter-control mb-5">
                          <div className="flex justify-between mb-2">
                            <label htmlFor={filter} className="flex items-center text-sm">
                              <Icon className={`mr-2 ${getFilterColor(filter)}`} />
                              {getFilterLabel(filter)}
                            </label>
                            <span className="filter-value text-xs bg-gray-900 px-2 py-1 rounded">
                              {filters[filter]}{getFilterUnit(filter)}
                            </span>
                          </div>
                          <input
                            type="range"
                            id={filter}
                            min={getFilterMin(filter)}
                            max={getFilterMax(filter)}
                            value={filters[filter]}
                            className="slider-thumb w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                            onChange={(e) => handleFilterChange(filter, e.target.value)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Effects Tab */}
                {activeTab === 'effects' && (
                  <div className="tab-panel">
                    <h2 className="text-xl font-semibold mb-4 text-purple-400">Color Effects</h2>

                    {['grayscale', 'sepia', 'invert'].map(filter => {
                      const Icon = getFilterIcon(filter);
                      return (
                        <div key={filter} className="filter-control mb-5">
                          <div className="flex justify-between mb-2">
                            <label htmlFor={filter} className="flex items-center text-sm">
                              <Icon className={`mr-2 ${getFilterColor(filter)}`} />
                              {getFilterLabel(filter)}
                            </label>
                            <span className="filter-value text-xs bg-gray-900 px-2 py-1 rounded">
                              {filters[filter]}%
                            </span>
                          </div>
                          <input
                            type="range"
                            id={filter}
                            min="0"
                            max="100"
                            value={filters[filter]}
                            className="slider-thumb w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                            onChange={(e) => handleFilterChange(filter, e.target.value)}
                          />
                        </div>
                      );
                    })}

                    <h2 className="text-xl font-semibold my-4 text-purple-400">Color Channels</h2>

                    {['red', 'green', 'blue'].map(filter => {
                      const Icon = getFilterIcon(filter);
                      return (
                        <div key={filter} className="filter-control mb-5">
                          <div className="flex justify-between mb-2">
                            <label htmlFor={filter} className="flex items-center text-sm">
                              <Icon className={`mr-2 ${getFilterColor(filter)}`} />
                              {getFilterLabel(filter)}
                            </label>
                            <span className="filter-value text-xs bg-gray-900 px-2 py-1 rounded">
                              {filters[filter]}%
                            </span>
                          </div>
                          <input
                            type="range"
                            id={filter}
                            min="0"
                            max="200"
                            value={filters[filter]}
                            className="slider-thumb w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                            onChange={(e) => handleFilterChange(filter, e.target.value)}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Presets Tab */}
                {activeTab === 'presets' && (
                  <div className="tab-panel">
                    <h2 className="text-xl font-semibold mb-4 text-purple-400">Filter Presets</h2>
                    <p className="text-gray-300 text-sm mb-6">Click on a preset to apply it to your photo</p>

                    <div className="grid grid-cols-2 gap-3">
                      {presetConfigs.map(preset => {
                        const Icon = preset.icon || FiImage;
                        return (
                          <button
                            key={preset.name}
                            onClick={() => applyPreset(preset.name)}
                            className={`bg-gradient-to-r ${preset.color} p-3 rounded-xl flex flex-col items-center justify-center h-24 transition-all ${activePreset === preset.name
                              ? 'ring-2 ring-purple-400 ring-offset-2 ring-offset-gray-800 transform scale-105'
                              : 'hover:shadow-lg'
                              }`}
                          >
                            <Icon className={`text-2xl mb-1 ${preset.iconColor}`} />
                            <span className="text-xs font-medium">{preset.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Photo Gallery */}
        <div className="mt-12 mb-20">
          <h2 className="text-2xl font-bold text-center mb-10 text-white">
            Photo Inspiration Gallery
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 w-full w-full mx-auto">
            {galleryPhotos.map((photoUrl, index) => (
              <div key={index} className="relative group cursor-pointer" onClick={() => {
                setPhoto(photoUrl);
                resetFilters();
                showNotification('Photo loaded from gallery', 'success');
              }}>
                <img
                  src={photoUrl}
                  alt="Random Photo"
                  className="rounded-xl shadow-lg w-full h-42 object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
                  <FiRepeat className="text-white text-xl transform rotate-90" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .slider-thumb::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #8B5CF6;
          cursor: pointer;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.3);
        }

        .slider-thumb::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #8B5CF6;
          cursor: pointer;
          border: none;
          box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.3);
        }

        .tab-panel {
          scrollbar-width: thin;
          scrollbar-color: #4B5563 #1F2937;
        }

        .tab-panel::-webkit-scrollbar {
          width: 6px;
        }

        .tab-panel::-webkit-scrollbar-track {
          background: #1F2937;
          border-radius: 3px;
        }

        .tab-panel::-webkit-scrollbar-thumb {
          background-color: #4B5563;
          border-radius: 3px;
        }
      `}</style>
    </div>
  );
};

export default StudioMusuPhotolab;